class CartDiscount extends HTMLElement {
  static ERROR_INVALID = 'Enter a valid discount code';
  static ERROR_NOT_APPLICABLE = "This discount code can't be applied to your cart";

  constructor() {
    super();
    this.addEventListener('click', this.onClick.bind(this));
    this.querySelector('form')?.addEventListener('submit', this.onSubmit.bind(this));
  }

  onSubmit(event) {
    event.preventDefault();
    const input = this.querySelector('[name="discount"]');
    if (!input || !input.value.trim()) return;

    const newCode = input.value.trim();
    const existingCodes = this.getAppliedCodes();

    if (existingCodes.includes(newCode.toLowerCase())) {
      this.showError(CartDiscount.ERROR_INVALID);
      return;
    }

    const allCodes = existingCodes.length ? `${existingCodes.join(',')},${newCode}` : newCode;
    this.applyDiscount(allCodes, input);
  }

  onClick(event) {
    const removeBtn = event.target.closest('[data-discount-remove]');
    if (!removeBtn) return;
    event.preventDefault();

    const codeToRemove = removeBtn.dataset.discountRemove.toLowerCase();
    const remaining = this.getAppliedCodes().filter((code) => code !== codeToRemove);
    this.applyDiscount(remaining.join(','), null);
  }

  getAppliedCodes() {
    return [...this.querySelectorAll('[data-discount-code]')].map((el) =>
      el.dataset.discountCode.toLowerCase()
    );
  }

  applyDiscount(discount, inputElement) {
    this.clearError();
    this.setLoading(true);

    const cartDrawerItems = document.querySelector('cart-drawer-items');
    const sections = cartDrawerItems
      ? cartDrawerItems.getSectionsToRender()
      : this.getSectionsToRender();

    const body = JSON.stringify({
      discount,
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status && parsedState.status === 422) {
          throw new Error(parsedState.message || parsedState.description || CartDiscount.ERROR_INVALID);
        }

        if (inputElement && discount) {
          this.validateDiscountApplied(parsedState, inputElement.value.trim());
          inputElement.value = '';
        }

        this.renderSections(parsedState, sections);

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper && parsedState.item_count !== undefined) {
          cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        }
      })
      .catch((error) => {
        this.showError(this.getErrorMessage(error));
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  validateDiscountApplied(parsedState, attemptedCode) {
    const code = attemptedCode.toLowerCase();
    const discountCodes = parsedState.discount_codes || [];
    const matched = discountCodes.find((d) => d.code.toLowerCase() === code);

    if (matched?.applicable === false) {
      throw new Error(CartDiscount.ERROR_NOT_APPLICABLE);
    }

    if (matched?.applicable === true) {
      return;
    }

    const hasCartLevelDiscount = (parsedState.cart_level_discount_applications || []).length > 0;
    if (hasCartLevelDiscount) {
      return;
    }

    if (parsedState.total_discount > 0) {
      return;
    }

    if (discountCodes.length === 0) {
      return;
    }

    throw new Error(CartDiscount.ERROR_INVALID);
  }

  getErrorMessage(error) {
    const message = error?.message || '';
    if (!message || message.includes('Translation missing')) {
      return CartDiscount.ERROR_INVALID;
    }
    return message;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'CartDrawer-Footer',
        section: 'cart-drawer',
        selector: '#CartDrawer-Footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  renderSections(parsedState, sections) {
    const sectionHTML = parsedState.sections?.['cart-drawer'];

    sections.forEach((section) => {
      if (!section.id) return;

      const elementToReplace = document.getElementById(section.id);
      if (!elementToReplace) return;

      if (section.section === 'cart-drawer') {
        if (!sectionHTML) return;
        const source = new DOMParser()
          .parseFromString(sectionHTML, 'text/html')
          .querySelector(section.selector);
        if (source) {
          elementToReplace.innerHTML = source.innerHTML;
        }
        return;
      }

      const otherSectionHTML = parsedState.sections?.[section.section];
      if (!otherSectionHTML) return;

      const container =
        elementToReplace.querySelector(section.selector) || elementToReplace;
      container.innerHTML = this.getSectionInnerHTML(otherSectionHTML, section.selector);
    });
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  showError(message) {
    const errorEl = this.querySelector('.cart-discount__error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
  }

  clearError() {
    const errorEl = this.querySelector('.cart-discount__error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
  }

  setLoading(loading) {
    this.classList.toggle('cart-discount--loading', loading);
    const applyBtn = this.querySelector('.cart-discount__apply');
    if (applyBtn) applyBtn.disabled = loading;
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
